import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '../store'

const AUTO_HIDE_MS = 3500

/**
 * Transient toast that appears when the user switches to a different
 * dataset (in global mode). Tells them that variable / range / timestep
 * have been reset to the new dataset's defaults — silent state loss is
 * worse than a visible reset.
 */
const DatasetSwitchNotice = () => {
  const datasetId = useAppStore((s) => s.datasetId)
  const scope = useAppStore((s) => s.scope)
  const sidebarWidth = useAppStore((s) => s.sidebarWidth) ?? 0
  const [visible, setVisible] = useState(false)
  const [label, setLabel] = useState('')
  const previousId = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // First mount — record but don't show.
    if (previousId.current === null) {
      previousId.current = datasetId
      return
    }
    if (previousId.current === datasetId) return
    previousId.current = datasetId

    // Never surface in collection scope — the dataset is locked so the user
    // can't cause the switch.
    if (scope === 'collection') return

    const mod = useAppStore.getState().getDatasetModule()
    setLabel(mod?.info ?? datasetId)
    setVisible(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setVisible(false), AUTO_HIDE_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [datasetId, scope])

  if (!visible) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: 50,
        left: sidebarWidth + 12,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid var(--rule)',
        borderLeft: '3px solid var(--teal)',
        borderRadius: 4,
        padding: '6px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        zIndex: 95,
        fontFamily: 'var(--card-sans)',
        fontSize: 11,
        color: 'var(--navy)',
        maxWidth: 320,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span>
        Switched to <strong style={{ fontWeight: 500 }}>{label}</strong> — variable, range and time reset.
      </span>
      <button
        onClick={() => setVisible(false)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--grey)',
          fontSize: 12,
          lineHeight: 1,
          padding: 0,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

export default DatasetSwitchNotice
